// Kit alerts — shared client + render helpers for the persisted alert stack.
//
// Single source of truth for Kit's proactive alerts: the kit_alerts table,
// written by the backend monitor (modules/kit_monitor.js) and served at
// /api/kit/actions/alerts/*. Both the Kit panel (shell/kit-panel.ts) and the
// dashboard Kit bar (pages/dashboard/kit-bar.ts) render from here, so a single
// alert never shows twice and dismiss/snooze sticks across reloads.
//
// PART 1 of the Kit Full Portal Access build. The one-click action button
// marks the alert actioned and deep-links to the relevant page; PART 2 will add
// in-conversation execution (send message, request payment, …).

import { api } from "@tkos/api-client";
import { iconMarkup, type IconName } from "@tkos/shared-ui";
import { navigate } from "./router";
import { promptCreateCrmRecord } from "./create-crm-record";

export interface KitAlert {
  id: string;
  type: string;
  jobId: string | null;
  customerId: string | null;
  title: string;
  detail: string | null;
  severity: number;
  actionLabel: string | null;
  actionTarget: string | null;
  entity: Record<string, unknown>;
  waitingSince: string | null;
  triggeredAt: string | null;
}

export type DismissReason = "handled" | "not_relevant" | "snooze";

// ── API ──────────────────────────────────────────────────────────────────────

export async function fetchActiveAlerts(retailerId: string): Promise<KitAlert[]> {
  try {
    const resp = await api<{ ok: boolean; alerts: KitAlert[] }>(
      `/kit/actions/alerts/active?retailerId=${encodeURIComponent(retailerId)}`,
    );
    return resp?.ok ? resp.alerts || [] : [];
  } catch {
    return [];
  }
}

export async function dismissAlert(
  retailerId: string, id: string, reason: DismissReason,
): Promise<boolean> {
  try {
    const resp = await api<{ ok: boolean }>(
      `/kit/actions/alerts/${encodeURIComponent(id)}/dismiss?retailerId=${encodeURIComponent(retailerId)}`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
    return !!resp?.ok;
  } catch {
    return false;
  }
}

export async function actionAlert(
  retailerId: string, id: string,
): Promise<{ ok: boolean; actionTarget?: string }> {
  try {
    const resp = await api<{ ok: boolean; actionTarget?: string }>(
      `/kit/actions/alerts/${encodeURIComponent(id)}/action?retailerId=${encodeURIComponent(retailerId)}`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return resp || { ok: false };
  } catch {
    return { ok: false };
  }
}

// ── Presentation helpers ───────────────────────────────────────────────────

export function iconForType(type: string): IconName {
  if (type.startsWith("balance") || type.startsWith("deposit") || type.startsWith("payment") || type.includes("deposit")) return "pound";
  if (type.startsWith("delivery")) return "truck";
  if (type.includes("message")) return "chat";
  if (type.startsWith("lead")) return "customer";
  if (type.startsWith("quote")) return "file";
  if (type.startsWith("snag")) return "alert-triangle";
  return "sparkle";
}

export function waitingLabel(waitingSince: string | null): string {
  if (!waitingSince) return "";
  const ms = Date.now() - new Date(waitingSince).getTime();
  if (!isFinite(ms) || ms < 0) return "";
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return "just now";
  if (h < 48) return `waiting ${h} hr${h === 1 ? "" : "s"}`;
  return `waiting ${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? "" : "s"}`;
}

export function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}

// ── Dismiss-menu open state ──────────────────────────────────────────────────
// Only one dismiss menu may be open at a time across every alert card (both
// surfaces). A single document listener closes it on any outside click.
let openMenuEl: HTMLElement | null = null;
let docCloserInstalled = false;

function closeOpenMenu(): void {
  if (openMenuEl) {
    openMenuEl.classList.remove("is-open");
    openMenuEl = null;
  }
}
function ensureDocCloser(): void {
  if (docCloserInstalled) return;
  docCloserInstalled = true;
  // Bubble-phase: a card's × button stops propagation, so this only fires for
  // clicks outside the currently-open menu.
  document.addEventListener("click", () => closeOpenMenu());
}

/**
 * Render one alert card into `host`. Wires the one-click action button
 * (marks actioned + navigates) and the dismiss menu (handled / not relevant /
 * snooze 24h). `onResolved(id)` is called after the card is actioned/dismissed
 * so the caller can drop it + refresh counts. `onNavigate` is optional extra
 * behaviour (e.g. close the panel) run just before navigating.
 */
export function renderAlertCard(
  host: HTMLElement,
  alert: KitAlert,
  retailerId: string,
  onResolved: (id: string) => void,
  onNavigate?: () => void,
): void {
  const card = document.createElement("div");
  card.className = "kit-alert";
  card.dataset["alertId"] = alert.id;
  const wait = waitingLabel(alert.waitingSince);
  card.innerHTML = `
    <span class="kit-alert__icon">${iconMarkup(iconForType(alert.type), { size: "sm" })}</span>
    <div class="kit-alert__body">
      <div class="kit-alert__title">${esc(alert.title)}</div>
      ${alert.detail ? `<div class="kit-alert__detail">${esc(alert.detail)}</div>` : ""}
      ${wait ? `<div class="kit-alert__wait">${esc(wait)}</div>` : ""}
      ${alert.actionLabel ? `<button type="button" class="kit-alert__act" data-role="act">${esc(alert.actionLabel)}</button>` : ""}
      ${alert.type === "competitor-mention" ? `<button type="button" class="kit-alert__act kit-alert__act--ghost" data-role="draft">Draft a reply</button>` : ""}
    </div>
    <div class="kit-alert__dismiss-wrap">
      <button type="button" class="kit-alert__dismiss" data-role="dismiss" aria-label="Dismiss" aria-haspopup="true">${iconMarkup("close", { size: "xs" })}</button>
      <div class="kit-alert__menu" data-role="menu">
        <button type="button" data-reason="handled">Handled</button>
        <button type="button" data-reason="not_relevant">Not relevant</button>
        <button type="button" data-reason="snooze">Snooze 24h</button>
      </div>
    </div>`;

  const actBtn = card.querySelector<HTMLButtonElement>('[data-role="act"]');
  actBtn?.addEventListener("click", () => {
    actBtn.disabled = true;
    void (async () => {
      const res = await actionAlert(retailerId, alert.id);
      onResolved(alert.id);
      if (onNavigate) onNavigate();
      const target = res.actionTarget || alert.actionTarget;
      if (target === "crm" && alert.customerId) {
        // Lead/contact alerts: go straight to the customer's full record
        // (navigate("customer", { id })) rather than the CRM list view.
        navigate("customer", { id: alert.customerId });
      } else if (alert.type === "lead-new" && !alert.customerId) {
        // A new lead with no CRM record yet — offer to create one (pre-filled
        // from the alert's entity) instead of dropping the retailer on an empty
        // CRM list. Falls back to the list if they pick "Maybe later".
        const ent = alert.entity || {};
        const created = await promptCreateCrmRecord({
          name: typeof ent["name"] === "string" ? (ent["name"] as string) : null,
          email: typeof ent["email"] === "string" ? (ent["email"] as string) : null,
        });
        if (!created) navigate("crm");
      } else if (target) {
        const params = alert.customerId ? { focus: alert.customerId } : undefined;
        navigate(target, params);
      }
    })();
  });

  // Competitor cards: "Draft a reply" drops the retailer into the conversation
  // with Kit's competitor-aware suggestions auto-opened (the suggest-replies
  // endpoint detects the competitor and folds in the playbook strategy).
  const draftBtn = card.querySelector<HTMLButtonElement>('[data-role="draft"]');
  draftBtn?.addEventListener("click", () => {
    if (onNavigate) onNavigate();
    const ent = alert.entity || {};
    const name = typeof ent["name"] === "string" ? (ent["name"] as string) : "";
    navigate("messages", { contact: name, suggest: "competitor" });
  });

  ensureDocCloser();
  const dismissBtn = card.querySelector<HTMLButtonElement>('[data-role="dismiss"]');
  const menu = card.querySelector<HTMLElement>('[data-role="menu"]');
  dismissBtn?.addEventListener("click", (e) => {
    e.stopPropagation(); // keep the doc-closer from instantly re-closing
    if (!menu) return;
    const isOpen = menu.classList.contains("is-open");
    closeOpenMenu(); // close whatever is open (this menu or another card's)
    if (!isOpen) {
      menu.classList.add("is-open");
      openMenuEl = menu;
    }
  });
  menu?.querySelectorAll<HTMLButtonElement>("[data-reason]").forEach((b) => {
    b.addEventListener("click", () => {
      closeOpenMenu();
      const reason = b.dataset["reason"] as DismissReason;
      void dismissAlert(retailerId, alert.id, reason);
      onResolved(alert.id);
    });
  });

  host.appendChild(card);
}
